import { PlusIcon } from '@heroicons/react/24/outline';

import { SUMMATIVETEST_MAX_TOPICS } from '@lessonPlanner/constants';


import { Button } from '../Button';

import InputSubtopic from './InputSubtopic';
import InputTopic from './InputTopic';

interface TopicsSectionProps {
  topics: { topic: string; subtopics: string[] }[];
  showSubtopics: boolean[];
  errors;
  emptySubtopicsIndex: {
    topicIndex: number | null;
    subtopicIndex: number[];
  };
  toggleSubtopicsVisibility: (topicIndex: number) => void;
  addSubtopic: (topicIndex: number) => void;
  addTopic: () => void;
  removeSubtopic: (topicIndex: number, subtopicIndex: number) => void;
  removeTopic: (topicIndex: number) => void;
  handleSubtopicChange: (topicIndex: number, subtopicIndex: number, value: string) => void;
  handleTopicChange: (topicIndex: number, value: string) => void;
  handleTrimUpdate: (index: number, value: string, type: 'topic' | 'subtopic', subIndex?: number) => void;
  isTopicEmpty: boolean;
}

export default function TopicsSection({
  topics,
  showSubtopics,
  errors,
  emptySubtopicsIndex,
  toggleSubtopicsVisibility,
  addSubtopic,
  addTopic,
  removeSubtopic,
  removeTopic,
  handleSubtopicChange,
  handleTopicChange,
  handleTrimUpdate,
  isTopicEmpty,
}: TopicsSectionProps) {
  const buttonClassName = `inline-block w-5/6 rounded border py-1 text-sm font-medium 'border-blue-600 bg-white text-blue-600 hover:bg-blue-500 hover:text-white active:bg-blue-600 active:text-white disabled:cursor-not-allowed disabled:border-blue-600 disabled:bg-white disabled:text-blue-600 disabled:opacity-60 disabled:hover:border-blue-600`;
  const buttonText = 'Add New Topic';

  return (
    <>
      {topics?.map((topicObj, index) => (
        <div key={index} className="col-span-full">
          <InputTopic
            canDelete={topics?.length > 1}
            error={errors?.topics?.[index]?.topic?.message}
            id={`topic-${index}`}
            label={`Topic ${index + 1}`}
            name={`topic-${index}`}
            value={topicObj.topic}
            onBlur={() => handleTrimUpdate(index, topicObj.topic, 'topic')}
            onRemove={() => removeTopic(index)}
            onChange={(value: string) => {
              handleTopicChange(index, value);
            }}
          />

          {!showSubtopics[index] && (
            <div className="mr-8 flex justify-end">
              <Button
                className="disabled:bg-gray-00 disabled:text-gray inline-block text-sm font-medium text-blue-600 outline-none focus:outline-none disabled:cursor-not-allowed"
                title="Click to add a new subtopic. Note: A new subtopic can only be added if the previous subtopic is not empty."
                type="button"
                onClick={() => toggleSubtopicsVisibility(index)}
              >
                <PlusIcon className="mr-2 inline h-4 w-4" />
                Add Subtopic
              </Button>
            </div>
          )}

          {showSubtopics[index] &&
            topicObj?.subtopics?.map((subtopic, subIndex) => {
              const emptySubtopicError =
                emptySubtopicsIndex.topicIndex === index &&
                emptySubtopicsIndex.subtopicIndex.includes(subIndex)
                  ? 'Please fill-out a subtopic before adding a new one.'
                  : '';
              const subtopicError =
                errors?.topics?.[index]?.subtopics?.[subIndex]?.message ||
                emptySubtopicError;

              return (
                <div key={subIndex}>
                  <InputSubtopic
                    error={subtopicError}
                    id={`topic-${index}-subtopic-${subIndex}`}
                    label={`Subtopic ${subIndex + 1}`}
                    name={`topic-${index}-subtopic-${subIndex}`}
                    value={subtopic}
                    onRemove={() => removeSubtopic(index, subIndex)}
                    onBlur={() =>
                      handleTrimUpdate(index, subtopic, 'subtopic', subIndex)
                    }
                    onChange={(value) =>
                      handleSubtopicChange(index, subIndex, value)
                    }
                  />

                  {subIndex === topicObj?.subtopics?.length - 1 && (
                    <div className="mr-8 flex justify-end">
                      <Button
                        className="disabled:bg-gray-00 disabled:text-gray inline-block text-sm font-medium text-blue-600 outline-none focus:outline-none disabled:cursor-not-allowed"
                        title="Click to add a new subtopic. Note: A new subtopic can only be added if the previous subtopic is not empty."
                        type="button"
                        onClick={() => addSubtopic(index)}
                      >
                        <PlusIcon className="mr-2 inline h-4 w-4" />
                        Add Subtopic
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      ))}

      <div className="col-span-full -mt-6 flex justify-center">
        <Button
          className={buttonClassName}
          type="button"
          disabled={
            topics?.length === 0 ||
            isTopicEmpty ||
            topics?.length >= SUMMATIVETEST_MAX_TOPICS ||
            topics.some((topic) => topic.topic.trim() === '')
          }
          onClick={addTopic}
        >
          {buttonText}
        </Button>
      </div>
    </>
  );
}